import { ErrorModal } from "@dexteel/mesf-core";
import { Box, Paper, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { urlParam, useUrlState, useUrlText } from "@/shared/hooks/useUrlState";
import {
	GROUP_LABELS,
	type StraightenerParamGroup,
} from "../../../production/straightener-common/parameterCatalog";
import { UnsavedChangesModal } from "../../../production/straightener-common/components/UnsavedChangesModal";
import {
	useStraightenerParamLimits,
	useUpsertStraightenerParameterConfig,
} from "../hooks/useStraightenerParamLimits";
import type { StraightenerParameterRow } from "../models/StraightenerParamLimit";
import { StraightenerLimitDetailViewer } from "./StraightenerLimitDetailViewer";
import { StraightenerLimitsTable } from "./StraightenerLimitsTable";
import { StraightenerLimitsToolbar } from "./StraightenerLimitsToolbar";

const DEFAULT_LAYOUT = { left: 60, right: 40 };

const URL_SCHEMA = {
	search: urlParam.string(),
	paramCode: urlParam.string(),
};

export const StraightenerLimitsManager = () => {
	const [error, setError] = useState("");
	const [notification, setNotification] = useState("");
	// The search and the parameter asked for, in the query string.
	const [
		{ search, paramCode },
		setFilters,
		{ dropValues, resetValues, isDefault },
	] = useUrlState(URL_SCHEMA);
	// The box types locally and the URL follows once typing stops.
	const [searchText, setSearchText] = useUrlText(search ?? "", (value) =>
		setFilters({ search: value || null }),
	);
	// The parameter the editor shows. It differs from the URL only while unsaved
	// edits hold a change that did not start with a click here (the sidebar link,
	// or Back to it), so the URL cannot discard them behind the guard's back.
	const [selectedCode, setSelectedCode] = useState<string | null>(paramCode);
	// Unsaved-changes guard: a click on another parameter (or close) is held
	// until the pending edits are confirmed away.
	const [detailDirty, setDetailDirty] = useState(false);
	const [pendingSelect, setPendingSelect] = useState<(() => void) | null>(null);

	const applySelection = (code: string | null) => {
		setSelectedCode(code);
		setFilters({ paramCode: code });
	};

	const { defaultLayout, onLayoutChange } = useDefaultLayout({
		id: "straightener-limits-manager-v1",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	const {
		data: parameters,
		isLoading,
		error: queryError,
	} = useStraightenerParamLimits();

	const { upsertConfig, isUpserting } = useUpsertStraightenerParameterConfig({
		onSuccess: () => setNotification("Parameter saved"),
		onError: (e) => setError(e.message),
	});

	// The whole catalog, straight from the backend (already ordered).
	const rows = useMemo<StraightenerParameterRow[]>(
		() =>
			(parameters ?? []).map((p) => ({
				code: p.Code,
				displayName: p.Name,
				group:
					GROUP_LABELS[p.GroupCode as StraightenerParamGroup] ?? p.GroupCode,
				valueType: p.ValueType,
				sendToPlc: !!p.SendToPlc,
				isRequired: !!p.IsRequired,
				readOnly: !!p.IsReadOnly,
				hasRolls: p.RollCount > 0,
				isProductDim: !!p.IsProductDim,
				min: p.MinValue,
				max: p.MaxValue,
			})),
		[parameters],
	);

	const filteredRows = useMemo(() => {
		const s = searchText.trim().toLowerCase();
		if (!s) return rows;
		return rows.filter(
			(r) =>
				r.displayName.toLowerCase().includes(s) ||
				r.group.toLowerCase().includes(s),
		);
	}, [rows, searchText]);

	const guardNavigation = (action: () => void) => {
		if (detailDirty) setPendingSelect(() => action);
		else action();
	};

	// The URL changed without a click here: follow it, or hold it behind the guard.
	// biome-ignore lint/correctness/useExhaustiveDependencies: only a URL change starts this
	useEffect(() => {
		if (paramCode === selectedCode) return;
		if (detailDirty) setPendingSelect(() => () => applySelection(paramCode));
		else setSelectedCode(paramCode);
	}, [paramCode]);

	// A link to a parameter the catalog does not hold would leave the editor empty.
	useEffect(() => {
		if (isLoading || paramCode === null) return;
		if (!rows.some((r) => r.code === paramCode)) {
			dropValues("paramCode");
			if (selectedCode === paramCode) setSelectedCode(null);
		}
	}, [isLoading, rows, paramCode, selectedCode, dropValues]);

	const selectedRow = useMemo(
		() => rows.find((r) => r.code === selectedCode) ?? null,
		[rows, selectedCode],
	);

	if (queryError) {
		return (
			<ErrorModal error={queryError.message} onHide={() => setError("")} />
		);
	}

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 130px)",
				display: "flex",
				flexDirection: "column",
				gap: 1,
				p: 1,
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Straightener Parameters
			</Typography>

			<Paper elevation={3}>
				<StraightenerLimitsToolbar
					onReset={() => resetValues(["search"])}
					canReset={!isDefault("search")}
					search={searchText}
					onSearchChange={setSearchText}
				/>
			</Paper>

			<Group
				id="straightener-limits-manager-group"
				orientation="horizontal"
				defaultLayout={defaultLayout ?? DEFAULT_LAYOUT}
				onLayoutChange={onLayoutChange}
				style={{ flex: 1, width: "100%", minHeight: 0 }}
			>
				<Panel id="left" minSize={30} style={{ paddingBottom: 5 }}>
					<Box sx={{ height: "100%", pr: 1 }}>
						<Paper elevation={3} sx={{ height: "100%", overflow: "hidden" }}>
							{isLoading ? (
								<CenteredLazyLoading />
							) : (
								<StraightenerLimitsTable
									rows={filteredRows}
									selectedCode={selectedCode}
									onSelect={(r) => {
										if (r.code === selectedCode) return;
										guardNavigation(() => applySelection(r.code));
									}}
									resync={pendingSelect == null}
								/>
							)}
						</Paper>
					</Box>
				</Panel>

				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 6,
							height: 40,
							borderRadius: 3,
							backgroundColor: "divider",
							"&:hover": { backgroundColor: "primary.main" },
						}}
					/>
				</Separator>

				<Panel id="right" minSize={30} style={{ paddingBottom: 5 }}>
					<Box sx={{ pl: 1, height: "100%" }}>
						<Paper elevation={3} sx={{ height: "100%", overflow: "hidden" }}>
							<StraightenerLimitDetailViewer
								row={selectedRow}
								isSaving={isUpserting}
								onSave={(code, min, max, sendToPlc, isRequired) =>
									upsertConfig({ code, min, max, sendToPlc, isRequired })
								}
								onClose={() => guardNavigation(() => applySelection(null))}
								onDirtyChange={setDetailDirty}
							/>
						</Paper>
					</Box>
				</Panel>
			</Group>

			<UnsavedChangesModal
				open={pendingSelect != null}
				onKeepEditing={() => {
					setPendingSelect(null);
					// The URL moved on its own: point it back at what is still being edited.
					if (paramCode !== selectedCode)
						setFilters({ paramCode: selectedCode });
				}}
				onDiscard={() => {
					const action = pendingSelect;
					setPendingSelect(null);
					action?.();
				}}
			/>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
